import { cn } from '@portals-platform/ui';

interface SummaryCardProps {
  label: string;
  value: number;
  selected?: boolean;
  onClick?: () => void;
}

function SummaryCard({ label, value, selected = false, onClick }: SummaryCardProps) {
  const className = 'rounded-[14px] border bg-card p-5 text-left';
  const content = (
    <>
      <div className="text-[11.5px] font-medium uppercase tracking-[0.02em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-3 text-[30px] font-semibold leading-none tabular-nums text-foreground">
        {value}
      </div>
    </>
  );

  if (!onClick) {
    return <div className={cn(className, 'border-border')}>{content}</div>;
  }

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        className,
        'transition-colors hover:bg-accent',
        selected ? 'border-primary' : 'border-border',
      )}
    >
      {content}
    </button>
  );
}

export interface CertificateSummaryCardsProps {
  totalElements: number;
  countsByStatus: Record<string, number>;
  status?: string;
  /** Omit for read-only cards. */
  onSelectStatus?: (status: string) => void;
  className?: string;
}

export function CertificateSummaryCards({
  totalElements,
  countsByStatus,
  status = 'all',
  onSelectStatus,
  className,
}: CertificateSummaryCardsProps) {
  const filterProps = (value: string) =>
    onSelectStatus
      ? {
          selected: status === value,
          onClick: () => onSelectStatus(status === value ? 'all' : value),
        }
      : {};

  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-4', className)}>
      <SummaryCard label="Total certificates" value={totalElements} />
      <SummaryCard
        label="Live certificates"
        value={countsByStatus.LIVE ?? 0}
        {...filterProps('LIVE')}
      />
      <SummaryCard
        label="Pending referrals"
        value={countsByStatus.REFERRED ?? 0}
        {...filterProps('REFERRED')}
      />
      <SummaryCard
        label="Pending renewals"
        value={countsByStatus.PENDING_RENEWAL ?? 0}
        {...filterProps('PENDING_RENEWAL')}
      />
    </div>
  );
}
